import React, { useState, useRef } from "react";
import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";
import { Icon } from "@elaniintc/goes-design-system";
import { parseCharacters } from "@/utils/format";

interface OptionTooltipProps {
  children: React.ReactNode;
  title: string;
  defaultOpen?: boolean;
  onChange?: (open: boolean) => void;
  danger?: boolean;
}

export const OptionTooltip: React.FC<OptionTooltipProps> = ({
  children,
  title,
  defaultOpen = false,
  onChange,
}) => {
  return (
    <Tooltip
      {...(defaultOpen ? { open: defaultOpen } : {})}
      delayDuration={0}
      {...(onChange ? { onOpenChange: onChange } : {})}
    >
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top" align="center">
        {title}
      </TooltipContent>
    </Tooltip>
  );
};

interface TextFieldIDProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  onKeyChange: (key: string) => void;
  keyValue?: string;
  limit?: number;
  canChangeKey?: boolean;
  error?: string;
}

export const TextFieldID: React.FC<TextFieldIDProps> = ({
  label,
  required = false,
  value,
  onChange,
  keyValue = "",
  limit,
  onKeyChange,
  canChangeKey = true,
  error,
}) => {
  const [locked, setLocked] = useState(true);
  const [localKey, setLocalKey] = useState(keyValue);
  const userEditedKey = useRef(false);

  // Only update localKey from prop if user hasn't edited it
  React.useEffect(() => {
    if (!userEditedKey.current) {
      setLocalKey(keyValue);
    }
  }, [keyValue]);

  const handleKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalKey(e.target.value);
    onKeyChange(e.target.value);
    userEditedKey.current = true;
  };

  const handleLockToggle = () => {
    setLocked((l) => !l);
  };

  return (
    <GroupTextField label={label} required={required} error={error}>
      <GroupTextFieldRow>
        <Input
          className="w-full"
          placeholder={label}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            if (!userEditedKey.current && canChangeKey) {
              const nextKey = parseCharacters(e.target.value.toLowerCase());
              setLocalKey(nextKey);
              onKeyChange(nextKey);
            }
          }}
          maxLength={limit}
          required={required}
        />
      </GroupTextFieldRow>
      <GroupTextFieldRow>
        <div className="flex items-center w-full relative">
          <Input
            className="w-full pr-12"
            placeholder="key"
            value={localKey}
            disabled={locked}
            onChange={handleKeyChange}
            group={canChangeKey ? "down-left" : "down"}
          />
          <OptionTooltip title="La key es el identificador técnico del campo. Se recomienda no modificarla si ya se ha utilizado en integraciones o automatizaciones.">
            <div className="absolute right-4 top-3 text-body">
              <Icon name="circle-info" type="far" />
            </div>
          </OptionTooltip>
        </div>
        {canChangeKey && (
          <Button
            variant="quiet"
            type="button"
            onClick={handleLockToggle}
            aria-label={
              locked ? "Desbloquear edición de key" : "Bloquear edición de key"
            }
          >
            <Icon name={locked ? "lock" : "lock-open"} type="far" />
          </Button>
        )}
      </GroupTextFieldRow>
    </GroupTextField>
  );
};

export default TextFieldID;
